// =============================================================================
// TrackerTable.jsx — versão original restaurada
// =============================================================================

import React, { useState, useMemo, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, TableSortLabel, Paper, IconButton, Typography,
  Box, Tooltip, LinearProgress, Collapse, Dialog,
  Menu, MenuItem, Checkbox, ListItemText, Divider, TextField, Button,
} from '@mui/material';
import EditIcon          from '@mui/icons-material/Edit';
import ContentCopyIcon   from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import FilterListIcon      from '@mui/icons-material/FilterList';
import SearchIcon          from '@mui/icons-material/Search';
import ClearIcon           from '@mui/icons-material/Clear';
import InfoOutlinedIcon    from '@mui/icons-material/InfoOutlined';
import RestartAltIcon      from '@mui/icons-material/RestartAlt';
import LanguageIcon        from '@mui/icons-material/Language';
import celfinetLogo      from '../../../assets/Cyient_Slate_Std_RGB.png';
import { getDisplayStatus } from '../utils/status';

// Labels apresentados no filtro "Area" (ex-"Side"). Valores internos
// mantêm-se East/West/None (ver SIDE_BUCKET no TrackerPage).
const AREA_LABELS = { East: 'East (TFinTF & VFinTF)', West: 'West (TFinVF)', None: 'None' };

// Coluna → (chave do filtro no estado, chave da lista de opções em filterOptions)
const COL_FILTER = {
  teamName:   { key: 'teams',        optKey: 'teams'    },
  side:       { key: 'sides',        optKey: 'sides',    labels: AREA_LABELS },
  technology: { key: 'technologies', optKey: 'techs'    },
  vendor:     { key: 'vendors',      optKey: 'vendors'  },
  feature:    { key: 'features',     optKey: 'features' },
  status:     { key: 'statuses',     optKey: 'statuses' },
  ownerNames: { key: 'owners',       optKey: 'owners'   },
};

// ── Pesquisa por título para o cabeçalho da coluna Activity ──────────────────
const TitleSearchMenu = ({ value, onApply }) => {
  const [anchor, setAnchor] = useState(null);
  const active = Boolean(value && value.trim());
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        aria-label="Search activity"
        sx={{ p: 0.25, ml: 0.25, color: active ? 'white' : 'rgba(255,255,255,0.55)' }}
      >
        <SearchIcon sx={{ fontSize: 15 }} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 1.5, width: 230 }}>
          <TextField
            autoFocus size="small" fullWidth
            placeholder="Search activity…"
            value={value ?? ''}
            onChange={(e) => onApply(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              endAdornment: value ? (
                <IconButton size="small" onClick={() => onApply('')} edge="end" sx={{ p: 0.25 }}>
                  <ClearIcon sx={{ fontSize: 15 }} />
                </IconButton>
              ) : null,
            }}
          />
        </Box>
      </Menu>
    </>
  );
};

// ── Filtro de data estilo Excel para os cabeçalhos Start/End ─────────────────
// Tipos: All / Today / This week / This month / This year / Before / After /
// Between / Specific dates (lista de checkboxes). Estado partilhado no
// TrackerPage (objecto { type, dates[], from, to }).
const EMPTY_DATE_FILTER = { type: 'all', dates: [], from: '', to: '' };
const DATE_TYPES = [
  { v: 'all',       label: 'All dates' },
  { v: 'today',     label: 'Today' },
  { v: 'thisWeek',  label: 'This week' },
  { v: 'thisMonth', label: 'This month' },
  { v: 'thisYear',  label: 'This year' },
  { v: 'before',    label: 'Before…' },
  { v: 'after',     label: 'After…' },
  { v: 'between',   label: 'Between…' },
  { v: 'list',      label: 'Specific dates…' },
];

const DateFilterMenu = ({ value, options, onApply }) => {
  const [anchor, setAnchor] = useState(null);
  const f = value ?? EMPTY_DATE_FILTER;
  const active = f.type && f.type !== 'all';
  const toggleDate = (d) => {
    const s = new Set(f.dates ?? []);
    if (s.has(d)) s.delete(d); else s.add(d);
    onApply({ ...f, dates: [...s] });
  };
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        aria-label="Filter date"
        sx={{ p: 0.25, ml: 0.25, color: active ? 'white' : 'rgba(255,255,255,0.55)' }}
      >
        <FilterListIcon sx={{ fontSize: 15 }} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        slotProps={{ paper: { sx: { maxHeight: 400 } } }}
      >
        <Box sx={{ p: 1.5, width: 235, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            select size="small" fullWidth label="Filter"
            value={f.type ?? 'all'}
            onChange={(e) => onApply({ ...f, type: e.target.value })}
            onKeyDown={(e) => e.stopPropagation()}
            SelectProps={{ MenuProps: { disablePortal: true } }}
          >
            {DATE_TYPES.map((o) => <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>)}
          </TextField>

          {(f.type === 'before' || f.type === 'after') && (
            <TextField
              type="date" size="small" fullWidth
              label={f.type === 'before' ? 'Before' : 'After'}
              InputLabelProps={{ shrink: true }}
              value={f.from ?? ''}
              onChange={(e) => onApply({ ...f, from: e.target.value })}
              onKeyDown={(e) => e.stopPropagation()}
            />
          )}

          {f.type === 'between' && (
            <>
              <TextField
                type="date" size="small" fullWidth label="From"
                InputLabelProps={{ shrink: true }}
                value={f.from ?? ''}
                onChange={(e) => onApply({ ...f, from: e.target.value })}
                onKeyDown={(e) => e.stopPropagation()}
              />
              <TextField
                type="date" size="small" fullWidth label="To"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: f.from || undefined }}
                value={f.to ?? ''}
                onChange={(e) => onApply({ ...f, to: e.target.value })}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </>
          )}

          {f.type === 'list' && (
            <Box sx={{ maxHeight: 200, overflow: 'auto', border: '0.5px solid', borderColor: 'divider', borderRadius: 1 }}>
              {options.length === 0 && <MenuItem disabled dense>No dates</MenuItem>}
              {options.map((d) => (
                <MenuItem key={d} dense onClick={() => toggleDate(d)}>
                  <Checkbox checked={(f.dates ?? []).includes(d)} size="small" sx={{ p: 0, mr: 1 }} />
                  <ListItemText primary={d} primaryTypographyProps={{ fontSize: '0.82rem' }} />
                </MenuItem>
              ))}
            </Box>
          )}

          {active && (
            <Button size="small" color="error" onClick={() => { onApply({ ...EMPTY_DATE_FILTER }); setAnchor(null); }}>
              Clear
            </Button>
          )}
        </Box>
      </Menu>
    </>
  );
};

// ── Filtro multi-select para um cabeçalho de coluna ──────────────────────────
const ColumnFilterMenu = ({ options, selected, labels, onApply }) => {
  const [anchor, setAnchor] = useState(null);
  const active = selected.length > 0;
  const toggle = (v) => {
    const set = new Set(selected);
    if (set.has(v)) set.delete(v); else set.add(v);
    onApply([...set]);
  };
  return (
    <>
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
        aria-label="Filter column"
        sx={{ p: 0.25, ml: 0.25, color: active ? 'white' : 'rgba(255,255,255,0.55)' }}
      >
        <FilterListIcon sx={{ fontSize: 15 }} />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        slotProps={{ paper: { sx: { maxHeight: 320 } } }}
      >
        {options.length === 0 && <MenuItem disabled dense>No options</MenuItem>}
        {options.map((o) => (
          <MenuItem key={o} dense onClick={() => toggle(o)}>
            <Checkbox checked={selected.includes(o)} size="small" sx={{ p: 0, mr: 1 }} />
            <ListItemText primary={labels?.[o] ?? o} primaryTypographyProps={{ fontSize: '0.82rem' }} />
          </MenuItem>
        ))}
        {active && (
          <>
            <Divider />
            <MenuItem dense onClick={() => { onApply([]); setAnchor(null); }}>
              <Typography variant="caption" color="error">Clear</Typography>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

const fmtDate = (raw) => {
  if (!raw) return '—';
  const s = String(raw);
  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return `${slash[3]}-${slash[1]}-${slash[2]}`;
  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, yyyy, a, b] = dash;
    return Number(a) > 12 ? `${yyyy}-${b}-${a}` : `${yyyy}-${a}-${b}`;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toISOString().slice(0, 10);
};

// Timestamp para ordenação — reutiliza a normalização do fmtDate (que já
// lida com "DD/MM/YYYY" e datas trocadas "YYYY-DD-MM") em vez de comparar a
// string em bruto. Sem isto, o Start/End ordenava mal porque comparava
// strings em formatos diferentes carácter a carácter, ignorando na prática
// o ano. Datas em falta/invalidas vão sempre para o fim.
const toTimestamp = (raw) => {
  if (!raw) return Infinity;
  const t = Date.parse(fmtDate(raw));
  return Number.isNaN(t) ? Infinity : t;
};

// Status mostrado ao utilizador — ver utils/status.js (fonte única,
// partilhada com a filtragem em TrackerPage).

// Mesmo mapeamento de negócio do filtro Side na barra principal
// (TrackerPage.jsx / TrackerFilters.jsx): West → TFinVF; East → TFinTF e
// VFinTF. Aqui na tabela mostra-se East/West (não o valor cru) para ficar
// consistente com o que já se vê no filtro.
const SIDE_BUCKET = { TFinTF: 'East', VFinTF: 'East', TFinVF: 'West' };

// Buckets (East/West) presentes na atividade, distintos e ordenados, a
// partir dos sites já gravados (server.tacs[].siteSides). Uma atividade
// pode ter mais que um (um único OMC_ID pode ter sites com Side diferente
// entre si — confirmado por SQL), por isso devolve sempre um array.
const getEntrySides = (entry) => {
  const buckets = new Set();
  (entry.servers ?? []).forEach((s) =>
    (s.tacs ?? []).forEach((t) =>
      Object.values(t.siteSides ?? {}).forEach((side) => {
        const bucket = SIDE_BUCKET[side];
        if (bucket) buckets.add(bucket);
      })
    )
  );
  return [...buckets].sort();
};

// Ordem das colunas alinhada com a ordem dos filtros do Excel:
// Activity · Area · Vendor · Technology · Team · Type · Status · Owner, com
// Start/End Date no fim (pedido do utilizador).
const COLUMNS = [
  { id: 'title',       label: 'Activity', sortKey: (e) => e.title?.toLowerCase() ?? '' },
  { id: 'side',        label: 'Area',     sortKey: (e) => getEntrySides(e).join(',').toLowerCase() },
  { id: 'vendor',      label: 'Vendor',   sortKey: (e) => e.vendor?.toLowerCase() ?? '' },
  { id: 'technology',  label: 'Tech',     sortKey: (e) => e.technology?.toLowerCase() ?? '' },
  { id: 'teamName',    label: 'Team',     sortKey: (e) => e.teamName?.toLowerCase() ?? '' },
  { id: 'feature',     label: 'Type',     sortKey: (e) => e.feature?.toLowerCase() ?? '' },
  { id: 'status',      label: 'Status',   sortKey: (e) => getDisplayStatus(e).toLowerCase() },
  { id: 'ownerNames',  label: 'Owner',    sortKey: (e) => (e.ownerNames ?? []).join(', ').toLowerCase() },
  { id: 'startDate',   label: 'Start',    sortKey: (e) => toTimestamp(e.startDate) },
  { id: 'endDate',     label: 'End',      sortKey: (e) => toTimestamp(e.endDate) },
  { id: '_actions',    label: '',         sortKey: null },
];

const headerCellSx = {
  fontWeight: 700, fontSize: '0.68rem',
  textTransform: 'uppercase', letterSpacing: 0.8,
  color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap',
  borderBottom: 'none',
};

const TrackerTable = ({ entries, loading, onRowClick, onDelete, onCopy, showClosed = false, canEditEntry = () => true, canDeleteEntry = () => true, userRole = null, userId = null, maximizedHeader = null, filters = {}, onFiltersChange = null, filterOptions = null, isMaximized = false, onMaximizeChange = null }) => {
  // Atualiza uma chave do filtro partilhado (estado vive no TrackerPage).
  const setFilterKey = (key, vals) => onFiltersChange?.({ ...filters, [key]: vals });
  // Maximizar/restaurar é controlado pela página (botão no topo, junto ao
  // "Show closed"), para ficar sempre visível — a coluna de ações ficava fora
  // do ecrã com o scroll horizontal.
  const setIsMaximized = (v) => onMaximizeChange?.(v);
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Multi-level sorting: lista ordenada de níveis [{ col, dir }].
  const [sortLevels,  setSortLevels]  = useState([{ col: 'startDate', dir: 'asc' }]);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  // ── Largura redimensionável da coluna Activity (arrastar a fronteira do
  // cabeçalho), para ler melhor os nomes longos das atividades. ──────────────
  const ACT_MIN = 120, ACT_MAX = 640;
  const [activityW, setActivityW] = useState(240);
  const resizeRef = useRef(null);
  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startW: activityW };
    const onMove = (ev) => {
      if (!resizeRef.current) return;
      const w = Math.min(ACT_MAX, Math.max(ACT_MIN, resizeRef.current.startW + (ev.clientX - resizeRef.current.startX)));
      setActivityW(w);
    };
    const onUp = () => {
      resizeRef.current = null;
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Ordenação "natural" multi-nível (sem Shift): cada clique numa coluna cicla
  //   1º clique → asc   2º clique → desc   3º clique → sai da ordenação
  // As colunas acumulam pela ordem em que foram clicadas (o número no
  // cabeçalho mostra a prioridade). O botão "Clear sort" remove tudo.
  const handleSort = (colId) => {
    setSortLevels((prev) => {
      const idx = prev.findIndex((l) => l.col === colId);
      if (idx === -1) return [...prev, { col: colId, dir: 'asc' }];
      if (prev[idx].dir === 'asc') {
        const next = [...prev];
        next[idx] = { col: colId, dir: 'desc' };
        return next;
      }
      return prev.filter((l) => l.col !== colId);   // desc → remove
    });
    setPage(0);
  };

  const displayed = useMemo(() => {
    const filtered = showClosed
      ? entries
      : entries.filter((e) => (e.status ?? 'Open') !== 'Closed');
    const levels = sortLevels
      .map((l) => ({ dir: l.dir, sortKey: COLUMNS.find((c) => c.id === l.col)?.sortKey }))
      .filter((l) => l.sortKey);
    if (!levels.length) return filtered;
    return [...filtered].sort((a, b) => {
      for (const l of levels) {
        const av = l.sortKey(a);
        const bv = l.sortKey(b);
        if (av < bv) return l.dir === 'asc' ? -1 : 1;
        if (av > bv) return l.dir === 'asc' ?  1 : -1;
      }
      return 0;
    });
  }, [entries, showClosed, sortLevels]);

  const start     = page * rowsPerPage;
  const paginated = rowsPerPage === -1 ? displayed : displayed.slice(start, start + rowsPerPage);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const Wrapper = isMaximized ? Dialog : Paper;
  const wrapperProps = isMaximized
    ? { fullScreen: true, open: true, onClose: () => setIsMaximized(false),
        PaperProps: { sx: { position: 'relative', p: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' } } }
    : { variant: 'outlined', sx: { borderRadius: 2, overflow: 'hidden', position: 'relative' } };

  return (
    <Wrapper {...wrapperProps}>
      {/* Em fullscreen mostra-se o cabeçalho/filtros da página (maximizedHeader),
          que já inclui o botão de restaurar (junto ao "Show closed"). */}
      {isMaximized && maximizedHeader && (
        <Box sx={{ mb: 1, flexShrink: 0 }}>
          {maximizedHeader}
        </Box>
      )}
      {loading && <LinearProgress sx={{ height: 2 }} />}
      {!loading && entries.length === 0 ? (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No activities yet. Click <strong>+ New Activity</strong> to start.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Wrapper à parte da área com scroll — a marca d'água fica fora do
              conteúdo que faz scroll, senão com "Rows: All" (muitas linhas)
              ela só aparecia uma vez a meio de toda a lista em vez de ficar
              sempre centrada na zona visível enquanto se faz scroll. */}
          <Box sx={{
            position: 'relative',
            ...(isMaximized ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}),
          }}>
            {/* Marca d'água — igual à do Gantt Chart */}
            <Box sx={{
              position: 'absolute',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none', zIndex: 0,
              width: '30%', minWidth: '200px', opacity: 0.05,
              display: 'flex', justifyContent: 'center'
            }}>
              <Box component="img" src={celfinetLogo} alt="" sx={{ width: '100%' }} />
            </Box>
            <TableContainer sx={isMaximized
              ? { flex: 1, minHeight: 0, overflow: 'auto' }
              : { maxHeight: 520 }
            }>
            <Table size="small">
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ ...headerCellSx, width: 60, padding: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                      {/* Dica de descoberta do multi-sort */}
                      <Tooltip
                        arrow
                        title="Click a column to sort. Click more columns to sort by several at once — the number shows the order. Click a column again to reverse, once more to remove. Use Clear sort to reset."
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 15, color: 'rgba(255,255,255,0.8)', cursor: 'help' }} />
                      </Tooltip>
                      {/* Clear sort — visível à esquerda (não fica fora do ecrã) */}
                      {sortLevels.length > 1 && (
                        <Tooltip title="Clear sort">
                          <IconButton size="small" onClick={() => setSortLevels([])} sx={{ color: 'white', p: 0.25 }}>
                            <RestartAltIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  {COLUMNS.map((col) => {
                    const cf = COL_FILTER[col.id];
                    const canFilter = Boolean(onFiltersChange);
                    const label = (col.id === 'side') ? 'Area' : col.label;   // ex-"Side" → "Area"
                    const sortIdx = sortLevels.findIndex((l) => l.col === col.id);
                    const sortLevel = sortIdx >= 0 ? sortLevels[sortIdx] : null;
                    return (
                    <TableCell
                      key={col.id}
                      sx={{
                        ...headerCellSx,
                        position: 'relative',
                        ...(col.id === 'title' ? { width: activityW, minWidth: activityW } : {}),
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {col.sortKey ? (
                          <>
                            <Tooltip title="Click to sort · again to reverse · again to remove" enterDelay={600} disableInteractive>
                              <TableSortLabel
                                active={Boolean(sortLevel)}
                                direction={sortLevel?.dir ?? 'asc'}
                                onClick={() => handleSort(col.id)}
                                sx={{ fontSize: 'inherit', letterSpacing: 'inherit', textTransform: 'inherit', fontWeight: 'inherit', color: 'inherit', '& .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.6) !important' }, '&.Mui-active': { color: 'white' } }}
                              >
                                {label}
                              </TableSortLabel>
                            </Tooltip>
                            {sortLevel && sortLevels.length > 1 && (
                              <Box component="span" sx={{
                                ml: 0.3, minWidth: 14, height: 14, px: '3px',
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                borderRadius: '7px', bgcolor: 'rgba(255,255,255,0.28)',
                                fontSize: '0.6rem', fontWeight: 700, lineHeight: 1, color: 'white',
                              }}>
                                {sortIdx + 1}
                              </Box>
                            )}
                          </>
                        ) : label}
                        {canFilter && col.id === 'title' && (
                          <TitleSearchMenu
                            value={filters.title}
                            onApply={(v) => onFiltersChange?.({ ...filters, title: v })}
                          />
                        )}
                        {canFilter && cf && (
                          <ColumnFilterMenu
                            options={filterOptions?.[cf.optKey] ?? []}
                            selected={filters[cf.key] ?? []}
                            labels={cf.labels}
                            onApply={(vals) => setFilterKey(cf.key, vals)}
                          />
                        )}
                        {canFilter && col.id === 'startDate' && (
                          <DateFilterMenu
                            value={filters.startFilter}
                            options={filterOptions?.startDates ?? []}
                            onApply={(v) => onFiltersChange?.({ ...filters, startFilter: v })}
                          />
                        )}
                        {canFilter && col.id === 'endDate' && (
                          <DateFilterMenu
                            value={filters.endFilter}
                            options={filterOptions?.endDates ?? []}
                            onApply={(v) => onFiltersChange?.({ ...filters, endFilter: v })}
                          />
                        )}
                      </Box>
                      {/* Pega de redimensionamento da coluna Activity */}
                      {col.id === 'title' && (
                        <Box
                          onMouseDown={startResize}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            position: 'absolute', top: 0, right: 0, height: '100%', width: '6px',
                            cursor: 'col-resize', userSelect: 'none',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
                          }}
                        />
                      )}
                    </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((entry) => {
                  const isClosed = (entry.status ?? 'Open') === 'Closed';
                  const displayStatus = getDisplayStatus(entry);
                  const hasComments = !!entry.comments?.trim();
                  const isExpanded = expandedIds.has(entry.id);
                  return (
                    <React.Fragment key={entry.id}>
                    <TableRow
                      hover
                      onClick={() => onRowClick(entry)}
                      sx={{
                        cursor: 'pointer',
                        '& td': { borderBottom: isExpanded ? 'none' : undefined },
                        opacity: isClosed ? 0.55 : 1,
                      }}
                    >
                      <TableCell sx={{ padding: 0, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        {hasComments && (
                          <Tooltip title={isExpanded ? 'Hide comments' : 'Show comments'}>
                            <IconButton size="small" onClick={() => toggleExpanded(entry.id)}>
                              <ExpandMoreIcon fontSize="small"
                                sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ width: activityW, maxWidth: activityW }}>
                        <Tooltip title={entry.title} placement="top" enterDelay={400}>
                          <Typography variant="body2" fontWeight={600} noWrap
                            sx={{ color: isClosed ? 'text.disabled' : 'inherit' }}>
                            {entry.title}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const sides = getEntrySides(entry);
                          return sides.length ? sides.join(', ') : '—';
                        })()}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {entry.vendor || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {entry.technology || '—'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        <Tooltip title={entry.teamName || ''} placement="top" enterDelay={400} disableHoverListener={!entry.teamName}>
                          <span>{entry.teamName || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        <Tooltip title={entry.feature || ''} placement="top" enterDelay={400} disableHoverListener={!entry.feature}>
                          <span>{entry.feature || '—'}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {displayStatus}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                        {(() => {
                          const owners = (entry.ownerNames?.length ? entry.ownerNames.join(', ') : entry.ownerName) || '—';
                          return (
                            <Tooltip title={owners} placement="top" enterDelay={400} disableHoverListener={owners === '—'}>
                              <span>{owners}</span>
                            </Tooltip>
                          );
                        })()}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {fmtDate(entry.startDate)}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {fmtDate(entry.endDate)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {canEditEntry(entry) && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => onRowClick(entry)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {/* Item 4 — atalho para o repositório: sempre visível,
                            só fica clicável (enabled) quando há link. */}
                        {(() => {
                          const repoUrl = entry.repositoryUrl?.trim();
                          const hasRepo = Boolean(repoUrl);
                          return (
                            <Tooltip title={hasRepo ? 'Open repository link' : 'No repository link'}>
                              {/* span porque um IconButton disabled não dispara o Tooltip */}
                              <span style={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                                <IconButton
                                  size="small"
                                  disabled={!hasRepo}
                                  onClick={() => window.open(repoUrl, '_blank', 'noopener,noreferrer')}
                                >
                                  {/* com link → azul (destaque); sem link → cinzento (disabled) */}
                                  <LanguageIcon fontSize="small" color={hasRepo ? 'primary' : undefined} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          );
                        })()}
                        {onCopy && (
                          <Tooltip title="Copy activity">
                            <IconButton size="small" onClick={() => onCopy(entry)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDeleteEntry(entry) && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => onDelete(entry.id)}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ opacity: isClosed ? 0.55 : 1 }}>
                      <TableCell sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }} colSpan={COLUMNS.length + 1}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 1.5, px: 2, bgcolor: 'action.hover', borderRadius: 1, my: 1 }}>
                            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              Comments
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {entry.comments}
                            </Typography>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
            </TableContainer>
          </Box>
          <TablePagination
            component="div"
            count={displayed.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50, { label: 'All', value: -1 }]}
            labelRowsPerPage="Rows:"
            sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
          />
        </>
      )}
    </Wrapper>
  );
};

export default TrackerTable;